import React, { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapPin, Warehouse } from 'lucide-react';

// Fix icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Icône personnalisée pour le Dépôt (Rouge)
const DepotIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Composant interne pour gérer le focus automatique (Zoom)
const AutoZoom = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  return null;
};

const DeliveryMap = ({ deliveries, depot }) => {
  const [routeCoords, setRouteCoords] = useState([]);

  // Préparation des points (Dépôt + Livraisons)
  const allPoints = useMemo(() => {
    const points = [];
    
    // 1. Ajouter le dépôt en premier
    if (depot && depot.latitude && depot.longitude) {
      points.push({ 
        lat: depot.latitude, 
        lng: depot.longitude, 
        title: "DÉPÔT / DÉPART", 
        isDepot: true 
      });
    }

    // 2. Ajouter les livraisons
    deliveries.forEach(d => {
      if (d.latitude && d.longitude) {
        points.push({ 
          lat: d.latitude, 
          lng: d.longitude, 
          title: d.client_name, 
          ref: d.reference, 
          isDepot: false 
        });
      }
    });

    return points;
  }, [deliveries, depot]);

  // Calcul des bornes pour le zoom
  const bounds = useMemo(() => allPoints.map(p => [p.lat, p.lng]), [allPoints]);

  // Récupération du tracé réel via OSRM
  useEffect(() => {
    if (allPoints.length < 2) {
      setRouteCoords([]);
      return;
    }

    const fetchRoute = async () => {
      // Format OSRM: {lon},{lat};{lon},{lat}...
      const coordsString = allPoints.map(p => `${p.lng},${p.lat}`).join(';');
      try {
        const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${coordsString}?overview=full&geometries=geojson`);
        const data = await res.json();
        
        if (data.routes && data.routes.length > 0) {
          // OSRM renvoie [lon, lat], Leaflet veut [lat, lon] -> On inverse
          const coordinates = data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
          setRouteCoords(coordinates);
        }
      } catch (error) {
        console.error("Erreur OSRM:", error);
        // Fallback: Ligne droite si l'API échoue
        setRouteCoords(allPoints.map(p => [p.lat, p.lng]));
      }
    };

    fetchRoute();
  }, [allPoints]);

  return (
    <div className="h-full w-full rounded-lg overflow-hidden border shadow-sm relative z-0">
      <MapContainer center={[46.6, 1.8]} zoom={5} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <AutoZoom bounds={bounds} />

        {allPoints.map((point, idx) => (
          <Marker 
            key={idx} 
            position={[point.lat, point.lng]}
            icon={point.isDepot ? DepotIcon : new L.Icon.Default()}
          >
            <Popup>
              <b>{point.title}</b>
              {!point.isDepot && <><br />{point.ref}<br />Stop #{idx}</>}
            </Popup>
          </Marker>
        ))}

        {routeCoords.length > 0 && (
          <Polyline positions={routeCoords} color="#4f46e5" weight={5} opacity={0.7} />
        )}
      </MapContainer>
    </div>
  );
};

export default DeliveryMap;